import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { TransactionTransferModal, TransactionTransferModalContext } from '@max/transaction/assignment/transaction-transfer-modal.component';
import { of, Subscription, throwError } from 'rxjs';
import { filter, first, mergeMap, pluck } from 'rxjs/operators';
import { BusinessTransactionOverrideItemKeyType } from '../../../Foundation/models/enums/business-transaction-override-item-key-type.enum';
import { ValidationType } from '../../../Foundation/models/enums/validation-type.enum';
import { NmvtisShellModalComponent } from '../../../titleregistration/nmvtis/components/nmvtis-shell/nmvtis-shell-modal.component';
import { NMVTISMessageModel } from '../../../titleregistration/nmvtis/models/nmvtis-message.model';
import { AAMVAIntegrationService } from '../../../titleregistration/services/aamvaIntegration.service';
import { BaseComponent } from '../../base-components/base.component';
import { Dictionary } from '../../models/dictionary.model';
import { ValidationGroupUIDetailViewModel } from '../../models/validation-results/validation-group-ui-detail.model';
import { ValidationResultModel } from '../../models/validation-results/validation-result.model';
import { ValidationModel } from '../../models/validation-results/validation.model';
import { BusinessTransactionService } from '../../services/business-transaction/business-transaction.service';
import { ActionTopic } from '../../services/dispatcher/action-topic.enum';
import { MessageKeys } from '../../services/dispatcher/message-keys';
import { MessageModel } from '../../services/dispatcher/message.model';
import { MvdModalService } from '../../services/modal/mvd-modal.service';
import { ValidationResultsService } from '../../services/validation-results/validation-results.service';
import { ValidationService } from '../../services/validation-results/validation.service';

@Component({
    selector: 'validation-results',
    templateUrl: './validation-results.html'
})
export class ValidationResultsComponent extends BaseComponent {
    @Input() validationResults: ValidationModel;
    @Input() validationCollectionName = '';
    @Input() validationResultsTitle = '';

    public displayErrors: ValidationResultModel[];
    public displayWarnings: ValidationResultModel[];
    public displayOverrides: ValidationResultModel[];
    public displayApprovals: ValidationResultModel[];
    public displayInformationalMessages: ValidationResultModel[];
    public displayConfirmationWarnings: ValidationResultModel[];
    public nmvtisExceptionMessages: ValidationResultModel[];
    public nmvtisValidationWarnings: ValidationResultModel[];
    public overridableComplianceSatisfyMessages: ValidationResultModel[];
    public complianceSatisfyMessages: ValidationResultModel[];
    public cdlApprovalMessages: ValidationResultModel[];
    public showWarningMessage = false;
    public showComponent: boolean;
    public activeWithdrawalAndPrivilegeNotId = 1738;
    public pendingWithdrawalId = 1740;
    public isLoading = false;

    private alertCount: number;
    private requestedEscalations: ValidationResultModel[] = new Array<ValidationResultModel>();
    private subscription: Subscription;
    private validationTypes: ValidationType = new ValidationType();
    private validationGroupUIMaps: ValidationGroupUIDetailViewModel[];
    private businessTransactionOverrideItemKeyType: BusinessTransactionOverrideItemKeyType = new BusinessTransactionOverrideItemKeyType();

    constructor(
        private businessTransactionService: BusinessTransactionService,
        private validationResultsService: ValidationResultsService,
        private modalService: MvdModalService,
        private router: Router,
        private validationService: ValidationService,
        private aamvaIntegrationService: AAMVAIntegrationService,
    ) {
        super();
        this.showComponent = false;

        this.subscription = this.validationResultsService.toggle$
            .pipe(
                pluck('validationCollectionName'),
                filter(valCollName => valCollName === this.validationCollectionName)
            )
            .subscribe(() => this.showComponent = !this.showComponent);
    }

    public ngOnInit() {}

    ngOnDestroy() {
        this.subscription.unsubscribe();
        super.ngOnDestroy();
    }

    public async ngOnChanges(): Promise<void> {
        this.showComponent = false;

        if (this.validationResults) {
            if (null == this.validationGroupUIMaps) {
                let validationGroupUIMapsResponse = await this.validationService.getValidationGroupUIMaps().pipe(first()).toPromise();
                this.validationGroupUIMaps = validationGroupUIMapsResponse.result;
            }

            this.displayErrors = this.validationResults.getErrors(this.validationGroupUIMaps);
            this.displayWarnings = this.validationResults.getWarnings(this.validationGroupUIMaps);
            this.displayOverrides = this.validationResults.getOverrides(this.validationGroupUIMaps);

            this.prepopulateExistingEscalationRequests(this.displayOverrides);
            this.displayApprovals = this.validationResults.getApprovals(this.validationGroupUIMaps);
            this.prepopulateExistingEscalationRequests(this.displayApprovals);
            this.displayInformationalMessages = this.validationResults.getInformationalMessages(this.validationGroupUIMaps);
            this.displayConfirmationWarnings = this.validationResults.getConfirmationWarnings(this.validationGroupUIMaps);
            this.nmvtisExceptionMessages = this.validationResults.getNMVTISErrors();
            this.nmvtisValidationWarnings = this.validationResults.getNMVTISValidationErrors();
            this.overridableComplianceSatisfyMessages = this.validationResults.getOverridableComplianceSatisfyMessages(this.validationGroupUIMaps);
            this.complianceSatisfyMessages = this.validationResults.getComplianceSatisfyMessages(this.validationGroupUIMaps);
            this.cdlApprovalMessages = this.validationResults.getCDLApprovalMessages(this.validationGroupUIMaps);
            // Message count
            this.alertCount = this.displayErrors.length +
                this.displayWarnings.length +
                this.displayInformationalMessages.length +
                this.displayConfirmationWarnings.length +
                this.displayOverrides.length +
                this.displayApprovals.length +
                this.nmvtisValidationWarnings.length +
                this.nmvtisExceptionMessages.length +
                this.overridableComplianceSatisfyMessages.length +
                this.complianceSatisfyMessages.length +
                this.cdlApprovalMessages.length;

            this.validationResultsService.sendCount({ validationCollectionName: this.validationCollectionName, count: this.alertCount });

            if (this.alertCount > 0) {
                this.showComponent = true;
            }
        } else {
            this.validationResultsService.sendCount({ validationCollectionName: this.validationCollectionName, count: 0 });
        }
    }

    public prepopulateExistingEscalationRequests = (validationModels: ValidationResultModel[]): void => {
        for (let validationModel of validationModels) {
            if (validationModel.state.vehicleId) {
                if (this.requestedEscalations.find(x => x.state.businessTansactionNumberId == validationModel.state.businessTansactionNumberId
                    && x.state.validationType == validationModel.state.validationType
                    && x.state.validationResourceId == validationModel.state.validationResourceId
                    && x.state.requestedValue == validationModel.state.requestedValue
                    && x.state.vehicleId == validationModel.state.vehicleId)) {
                    validationModel.state.escalationRequested = true;
                }
            } else if (this.requestedEscalations.find(x => x.state.businessTansactionNumberId == validationModel.state.businessTansactionNumberId
                && x.state.validationType == validationModel.state.validationType
                && x.state.validationResourceId == validationModel.state.validationResourceId
                && x.state.requestedValue == validationModel.state.requestedValue)) {
                validationModel.state.escalationRequested = true;
            }
        }
    };

    public closeInformation = (i: number): void => {
        this.displayInformationalMessages.splice(i, 1);
        this.alertCount = this.alertCount - 1;
    };

    private makeString(value: number|string) {
        if (value === null || value === undefined) {
            return null;
        } else {
            return value.toString();
        }
    }

    public requestOverride = (override: ValidationResultModel): void => {
        this.isLoading = true;
        let businessTransactionNumberId = this.makeString(override.state.businessTansactionNumberId);
        let validationResourceId = this.makeString(override.state.validationResourceId);
        let originalValue = this.makeString(override.state.originalValue);
        let requestedValue = this.makeString(override.state.requestedValue);
        let vehicleId = override.state.vehicleId;
        let message = override.errorMessage;

        if (vehicleId && businessTransactionNumberId && validationResourceId && originalValue && requestedValue) {
            this.businessTransactionService.createOverrideWithKey(businessTransactionNumberId, validationResourceId, originalValue, requestedValue, vehicleId, this.businessTransactionOverrideItemKeyType.Vehicle.id, message)
                .subscribe(
                    () => {
                        this.isLoading = false;
                        override.state.escalationRequested = true;
                        this.requestedEscalations.push(override);
                    }, err => {
                        this.isLoading = false;
                        console.log(err);
                    });
        } else if (businessTransactionNumberId && validationResourceId && originalValue && requestedValue) {
            this.businessTransactionService.createOverride(businessTransactionNumberId, validationResourceId, originalValue, requestedValue, message)
                .subscribe(
                    () => {
                        this.isLoading = false;
                        override.state.escalationRequested = true;
                        this.requestedEscalations.push(override);
                    }, err => {
                        this.isLoading = false;
                        console.log(err);
                    });
        }
    };

    public requestApproval = (approval: ValidationResultModel): void => {
        let businessTransactionNumberId = approval.state.businessTansactionNumberId.toString();
        let validationResourceId = approval.state.validationResourceId.toString();
        let requestedValue = approval.state.requestedValue.toString();
        let message = approval.errorMessage;
        this.businessTransactionService.createApproval(businessTransactionNumberId, validationResourceId, requestedValue, message)
            .subscribe(result => {
                approval.state.escalationRequested = true;
                this.requestedEscalations.push(approval);
            }, err => {
                console.log(err);
            });
    };

    public showRequestAll = (): boolean => {
        let requestableValidationResults = this.validationResults && this.validationResults.validationResults.filter(x => (x.state.validationType == this.validationTypes.Override.value || x.state.validationType == this.validationTypes.Approval.value) && !x.state.escalationRequested);
        let showButton = requestableValidationResults && (requestableValidationResults.length > 0);

        return showButton;
    };

    public requestAll = async (): Promise<void> => {
        const requestableValidationResults = this.validationResults.validationResults.filter(x => (x.state.validationType == this.validationTypes.Override.value || x.state.validationType == this.validationTypes.Approval.value) && !x.state.escalationRequested);
        this.isLoading = true;
        await this.businessTransactionService.createAllEscalations(requestableValidationResults).pipe(first()).toPromise();
        this.isLoading = false;

        for (const escalation of requestableValidationResults) {
            escalation.state.escalationRequested = true;
            this.requestedEscalations.push(escalation);
        }
    };

    public approveConfirmationWarning = (confirmationWarning: ValidationResultModel): void => {
        let self = this;
        let businessTransactionNumberId = confirmationWarning.state.businessTansactionNumberId.toString();
        let validationResourceId = confirmationWarning.state.validationResourceId.toString();
        let requestedValue = confirmationWarning.state.requestedValue.toString();
        let vehicleId = confirmationWarning.state.vehicleId;
        let message = confirmationWarning.errorMessage;

        if (vehicleId) {
            this.businessTransactionService.confirmValidationWithKey(businessTransactionNumberId, validationResourceId, requestedValue, vehicleId, this.businessTransactionOverrideItemKeyType.Vehicle.id, message)
                .subscribe(result => {
                    (confirmationWarning as any).confirmed = true;
                    let values: Dictionary = null;
                    if (result) {
                        values = new Dictionary([]);
                        values.add(MessageKeys.ValidationResourceId, result.validationResourceId);
                    }
                    self.dispatcher.publish({ topic: ActionTopic.ConfirmationWarningApproved, values: values });
                }, err => {
                    console.log(err);
                });
        } else {
            this.businessTransactionService.confirmValidation(businessTransactionNumberId, validationResourceId, requestedValue, message)
                .subscribe(result => {
                    (confirmationWarning as any).confirmed = true;
                    let values: Dictionary = null;
                    if (result) {
                        values = new Dictionary([]);
                        values.add(MessageKeys.ValidationResourceId, result.validationResourceId);
                    }
                    self.dispatcher.publish({ topic: ActionTopic.ConfirmationWarningApproved, values: values });
                }, err => {
                    console.log(err);
                });
        }
    };

    public cancelConfirmationWarning = (confirmationWarning): void => {
        let index = this.displayConfirmationWarnings.findIndex(c => c.errorMessage === confirmationWarning.errorMessage);
        this.displayConfirmationWarnings.splice(index, 1);
    };

    public async showNmvtisMessagesAgain() {
        var businessTransactionId = this.validationResults.parsedBody.validationResults[0].state.businessTansactionNumberId;
        try {

            var messageId = +this.nmvtisValidationWarnings.pop().state.requestedValue;
            this.nmvtisValidationWarnings = null; // hide the warning now.
            this.dispatcher.publish({ topic: ActionTopic.ValidationSuccess, values: null });
            var csrmessage = <NMVTISMessageModel> await this.aamvaIntegrationService.getNmvtisInteractionMessageById(businessTransactionId, messageId).pipe(first()).toPromise();
            if (csrmessage) {
                this.modalService.open(NmvtisShellModalComponent, csrmessage).afterClosed().pipe(filter(result => !!result)).subscribe(
                    () => {
                        this.dispatcher.publish({ topic: ActionTopic.ValidationSuccess, values: null });
                    });
            } else {
                await this.aamvaIntegrationService.resendNmvtisInteractionMessage(businessTransactionId).pipe(first()).toPromise();
            }
        } catch (ex) {
            await this.aamvaIntegrationService.resendNmvtisInteractionMessage(businessTransactionId).pipe(first()).toPromise();
        }
    }

    public showNmvtisModal = (error: any): void => {
        // TODO: find out why the following commented code is not working
        let data = new NMVTISMessageModel();
        data.businessTransactionId = this.validationResults.parsedBody.businessTransactionId;
        data.isClean = this.validationResults.parsedBody.isClean;
        data.nmvtisRequest = this.validationResults.parsedBody.nmvtisRequest;
        data.nmvtisResponse = this.validationResults.parsedBody.nmvtisResponse;
        data.shouldAbandonTransation = this.validationResults.parsedBody.shouldAbandonTransation;
        data.integrationInteractionMessageTypeId = this.validationResults.parsedBody.integrationInteractionMessageTypeId;
        data.interactionManagerMessageId = this.validationResults.parsedBody.interactionManagerMessageId;
        data.interactionManagerMessageContent = this.validationResults.parsedBody.interactionManagerMessageContent;
        this.modalService.open(NmvtisShellModalComponent, data).afterClosed().pipe(filter(result => !!result)).subscribe(
            () => {
                // Clear the NMVTIS validation message.
                this.nmvtisExceptionMessages = this.nmvtisExceptionMessages.filter(obj => obj !== error);
                if (!this.nmvtisExceptionMessages.length) {
                    this.dispatcher.publish({ topic: ActionTopic.ValidationSuccess, values: null });
                }
            });
    };

    public navigateToCompliance = (validationResult: any): void => {
        let customerNumberId = validationResult.customerNumberId ? validationResult.customerNumberId : validationResult.state.customerNumberId;
        let vehicleId = validationResult.vehicleId ? validationResult.vehicleId : validationResult.state.vehicleId;
        let businessTansactionNumberId = validationResult.state.businessTansactionNumberId.toString();

        if (customerNumberId) {
            this.router.navigate(['/compliance/direct/c/', customerNumberId, businessTansactionNumberId]);
        } else if (vehicleId) { // TODO: Please verify if the route is correct
            this.router.navigate(['/compliance/direct/v/', vehicleId, businessTansactionNumberId]);
        }
    };

    public clearWarning(i: number): void {
        this.displayWarnings.splice(i, 1);
        this.alertCount -= 1;
        this.validationResultsService.sendCount({ validationCollectionName: this.validationCollectionName, count: this.alertCount });
        if (this.alertCount === 0) {
            this.close();
        }
    }

    public close = (): void => {
        this.validationResultsService.toggle({ validationCollectionName: this.validationCollectionName });
    };

    /**
     * publishes a message to indicate we are changing validation groups so any subscribers can respond to the change
     */
    public sendValidationGroupMessage = async (groups: any): Promise<void> => {
        let eventArgs: Dictionary = new Dictionary([]);
        eventArgs.add(MessageKeys.ValidationGroups, groups);
        let message = new MessageModel();
        message.topic = ActionTopic.ValidationGroupChange;
        message.values = eventArgs;
        this.dispatcher.publish(message);
    };

    public openTransactionTransferConfirmationModal(override: ValidationResultModel) {
        let data = new TransactionTransferModalContext(override.state.businessTansactionNumberId, override.state.requestedValue, 'CDL');
        this.modalService.open(TransactionTransferModal, data)
            .afterClosed()
            .subscribe(
                transferredTransaction => {
                    if (transferredTransaction) {
                        // Navigate the user to the Queue Dashboard since they no longer can operate on this transaction
                        this.router.navigate(['/']);
                    }
                });
    }
}
